import { Honeybadger } from "@honeybadger-io/react";
import axios from "./services/axios/backendAxios";
// eslint-disable-next-line no-restricted-imports
import * as baseAxios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { RouterProvider } from "react-router-dom";
import { resetUser, updateUser } from "./store/reducers/currentUserSlice";
import router from "./router/router";
import { AuthUser, decodeJWT } from "@mysnowscape/snow-lib";
import errors from "./services/errors";
import { tokenSupplier } from "./services/axios/webAuth";
import CurrentCompanyManager from "./CurrentCompanyManager";
import AuthContext, {
  AuthState,
  makeAuthState,
  defaultAuthState,
} from "./AuthContext";
/**
 * Auth manager's job is to block children loading until auth is ready.  It is also responsible for setting the currentUser value when
 * the auth state changes.
 * WARNING: It does NOT care if the user is logged in or not.  It's only in charge of the items above.
 * Notes:
 * - will not render children until auth is ready
 * - currentUser might be undefined or null when the children are rendered (if the /user/me request is running). This is necessary since
 *     it's possible to be logged in but have no user yet (had an issue logging in and or didn't complete user setup).
 * - authUser will be null if the user is logged out.
 */
const AuthenticationManager2 = () => {
  const [authState, setAuthState] = useState<AuthState>(defaultAuthState);
  const dispatch = useDispatch();

  // Callback to check if we are logged in and if we have a db user:
  const checkToken = useCallback(async () => {
    const t = await tokenSupplier.getToken();
    if (t !== null) {
      const authUser = decodeJWT(t) as AuthUser;
      setAuthState(makeAuthState(authUser, false));

      // Now look up the user
      try {
        const r = await axios.get("/user/me");
        // dispatch(setCurrentCompany(null));
        dispatch(
          updateUser({
            ...r.data.user,
            is_impersonating: r.data.is_impersonating ?? false,
            // currentCompanyId: null,
          }),
        );
      } catch (err) {
        if (baseAxios.isAxiosError(err) && err.response?.status === 404) {
          console.log(
            "Logged in user has no database user so we are resetting currentUser in Redux.",
          );
          dispatch(resetUser(null));
        } else {
          errors.report(err, { action: "attempting to load current user" });
        }
      }
    } else {
      setAuthState(makeAuthState(null, false)); // Done loading, but logged out
    }
  }, [dispatch, setAuthState]);

  // If the auth state changes, check if we can still get a token:
  useEffect(() => {
    checkToken();
    const handle = tokenSupplier.subscribe(() => {
      checkToken(); // if our auth state has changed, recheck if we have an auth token
    });
    return () => {
      tokenSupplier.unsubscribe(handle);
    };
  }, [checkToken]);

  // Let HoneyBadger know about our current user
  useEffect(() => {
    Honeybadger.setContext({
      user_id: authState.authUser?.user_id,
    });
  }, [authState.authUser?.user_id]);

  if (authState.authLoading) return null;

  console.log("AM2: auth loading, sending the following object", authState);

  return (
    <AuthContext.Provider value={authState}>
      <CurrentCompanyManager>
        <RouterProvider router={router} />
      </CurrentCompanyManager>
    </AuthContext.Provider>
  );
};

export default AuthenticationManager2;
